import type { DurationOption } from "@p0security/shared/configuration/types";
import { isPermissionType } from "@p0security/shared/types/permission";
import {
  APPROVAL_DURATION_CATEGORY,
  ApprovalRule,
  Directory,
  type DurationLimitedApproval,
  Filter,
  IdpGroup,
  IntegrationResourceRule,
  Policy,
  ResourceRule,
} from "@p0security/shared/types/policy/types";
import { isKeyOf } from "@p0security/util/types/is";
import { resolveTenantMaxDurations } from "components/Admin/useDurationSettings";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { useCallback } from "react";

import {
  DEFAULT_NEW_REQUESTOR,
  DEFAULT_NEW_RESOURCE,
  filterKeyOptionsByResource,
  filterTypeOptionsByResource,
} from "./constants";

export const EXAMPLE_RULE = {
  name: "Example Rule",
  requestor: {
    type: "group",
    directory: "okta",
    id: "00g5j4jojlGZMzfhM697",
    label: "Engineering",
  },
  resource: {
    type: "integration",
    service: "snowflake",
  },
  approval: [
    {
      type: "group",
      id: "dataops@yourorg.com",
      label: "Data Ops Team",
      directory: "workspace",
      options: {
        allowOneParty: false,
      },
    },
  ],
};

export const EXAMPLE_DOCUMENT = yaml.dump([EXAMPLE_RULE], { flowLevel: 4 });

export const DEFAULT_RULE = {
  name: "Default p0 rule",
  requestor: DEFAULT_NEW_REQUESTOR,
  resource: DEFAULT_NEW_RESOURCE,
  approval: [
    {
      options: {
        allowOneParty: false,
        requireReason: false,
      },
      type: "p0",
    },
  ],
};

export const DEFAULT_DOCUMENT = yaml.dump([DEFAULT_RULE], { flowLevel: 4 });

export enum PoliciesView {
  YAML = "yaml",
  TABULAR = "tabular",
}

// All the logic for generating unique values and filters

export const getUniqueResources = (jsonRules: any[]) => {
  return Array.from(
    new Set(jsonRules.map((rule) => rule.resource && rule.resource.type))
  );
};

export const getResourceFilters = (uniqueResources: string[]) => {
  return uniqueResources.map((resource) => ({
    text: resource,
    value: resource,
  }));
};

// Typescript can't narrow integration resource filters from the keyed union to
// the single Filter value type — the cast localizes the workaround.
export const getResourceFilterEntries = (
  resource: ResourceRule | undefined
): [string, Filter][] => {
  if (resource?.type !== "integration") return [];
  return Object.entries(resource.filters ?? {}) as [string, Filter][];
};

export const useOnFilter = (dataIndex: string) => {
  return useCallback(
    (value: any, record: any) => {
      if (record[dataIndex] === undefined) {
        return false;
      }
      const yamlContent = yaml.dump(record[dataIndex]);
      return yamlContent.toLowerCase().includes(value.toLowerCase());
    },
    [dataIndex]
  );
};

export const isValidApproval = (approval: ApprovalRule) => {
  switch (approval.type) {
    case "group":
      return approval.groups.every((g) => !!g.directory && !!g.id && !!g.label);
    default:
      return true;
  }
};

export const isValidResourceFilterType = (
  type: string,
  service: IntegrationResourceRule["service"]
) => {
  return isKeyOf(filterTypeOptionsByResource, service)
    ? Boolean(filterTypeOptionsByResource[service]?.[type])
    : false;
};

export const generateFilterKeyOptions = (
  service: IntegrationResourceRule["service"],
  filterType: string
) => {
  if (!isPermissionType(service)) {
    return undefined;
  }
  const options = filterKeyOptionsByResource[service];
  if (!options || !(filterType in options)) {
    return undefined;
  }
  return options[filterType];
};

export const buildIdpGroupId = (group: IdpGroup) => {
  return `${group.directory}_${group.id}`;
};

export const parseIdpGroupId = (id: string): IdpGroup => {
  const [directory, idpId] = id.split("_");

  if (!directory || !idpId) {
    throw new Error("Invalid idp group id");
  }

  return {
    directory: directory as Directory,
    id: idpId,
    label: "",
  };
};

/** Deterministic hash of a policy, producing a short URL-safe string. */
export function hashRule(rule: Policy): string {
  const str = stringify(rule) ?? "";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

type TenantMaxDurations = ReturnType<typeof resolveTenantMaxDurations>;

/** The tenant limit for an action: the standing max for automatic actions,
 * the tenant max for the rest */
export const defaultMaxFor = (
  type: DurationLimitedApproval["type"],
  tenant: TenantMaxDurations
): DurationOption =>
  APPROVAL_DURATION_CATEGORY[type] === "automatic"
    ? tenant.standing
    : tenant.max;
